import { useMutation, useQuery, useQueries, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { api } from 'shared/api/client';
import { errorHandler, showMessage } from 'shared/feedback/messages';
import { i18n } from 'shared/i18n';
import { documentKeys } from './keys';

interface ImageFileShape {
  fileType?: string;
  fileContent?: string;
  filename?: string;
  superId?: string;
  revisionId?: string;
  createdAt?: string | null;
  documentSuperId?: number | string;
  type?: string;
  [key: string]: unknown;
}

interface ImageMetaShape {
  size?: number;
  width?: number;
  height?: number;
  [key: string]: unknown;
}

interface ImagePayload {
  file?: ImageFileShape;
  originalMetadata?: ImageMetaShape;
  occurences?: unknown[];
}

interface ImageMapEntry {
  file: ImageFileShape;
  metadata: ImageMetaShape;
  occurences: unknown;
}

export const useImage = (revisionId: string | undefined, type = 'thumbnail') =>
  useQuery({
    queryKey: documentKeys.image(revisionId),

    queryFn: async () => {
      if (!revisionId) {
        return null;
      }
      const response = await api.GET('/images/{id}/{imageType}', { params: { path: { id: revisionId, imageType: type } } });
      const data = response.data as ImagePayload;
      return { file: data.file, metadata: data.originalMetadata, occurences: data.occurences };
    },

    meta: { errorHandler },
    enabled: !!revisionId,
  });

export const useBatchImages = (documentSuperId: number | string, revisionIds: string[] = [], imageType = 'thumbnail') => {
  const CHUNK_SIZE = 20;

  const chunks = useMemo(() => {
    if (!revisionIds.length) return [];
    const result: string[][] = [];
    for (let i = 0; i < revisionIds.length; i += CHUNK_SIZE) {
      result.push(revisionIds.slice(i, i + CHUNK_SIZE));
    }
    return result;
  }, [revisionIds]);

  const queries = useQueries({
    queries: chunks.map((chunk, index) => ({
      queryKey: documentKeys.imagesBatch(documentSuperId, imageType, index, chunk),

      queryFn: async () => {
        const res = await api.POST('/images/batch', {
          body: {
            revisionIds: chunk,
            documentSuperId: Number(documentSuperId),
            imageType,
          },
        });
        return res.data;
      },

      enabled: !!documentSuperId && chunk.length > 0,
      meta: { errorHandler },
      refetchOnWindowFocus: false,
    })),
  });

  const imageDataMap = useMemo(() => {
    return queries.reduce<Record<string, ImageMapEntry>>((acc, query) => {
      if (query.data) {
        query.data.forEach((img) => {
          const revisionId = (img.file as { revisionId: string }).revisionId;
          acc[revisionId] = {
            file: img.file as ImageFileShape,
            metadata: img.originalMetadata as ImageMetaShape,
            occurences: img.occurences,
          };
        });
      }
      return acc;
    }, {});
  }, [queries]);

  const isLoading = queries.some((q) => q.isLoading);
  const isFetching = queries.some((q) => q.isFetching);

  return { data: imageDataMap, isLoading, isFetching };
};

export const useImagesInDocument = (documentId?: number | string) =>
  useQuery({
    queryKey: [...documentKeys.imagesAll],

    queryFn: async () => {
      const response = await api.GET('/images/document/{documentId}/{imageType}', {
        params: { path: { documentId: Number(documentId), imageType: 'thumbnail' } },
      });
      return response.data;
    },

    enabled: !!documentId,
    meta: { errorHandler },
  });

export const useChangeImageName = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      documentSuperId,
      imageSuperId,
      imageRevisionId,
      newName,
    }: {
      documentSuperId: number | string;
      imageSuperId: string;
      imageRevisionId: string;
      newName: string;
    }) => {
      return api.POST('/documents/{superId}/actions', {
        params: { path: { superId: Number(documentSuperId) } },
        body: { action: 'rename_image', superId: imageSuperId, revisionId: imageRevisionId, newName },
      });
    },

    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: documentKeys.image(variables.imageRevisionId),
      });

      queryClient.invalidateQueries({
        queryKey: [...documentKeys.imagesAll],
      });
      showMessage('success', i18n('image-queries:image-rename-success'));
    },

    onError: errorHandler,
  });
};

export const useDeleteImage = () => {
  return useMutation({
    mutationFn: ({ documentSuperId, imageRevisionId }: { documentSuperId: number | string; imageRevisionId: string }) => {
      return api.POST('/documents/{superId}/actions', {
        params: { path: { superId: Number(documentSuperId) } },
        body: { action: 'delete_image', revisionId: imageRevisionId },
      });
    },
  });
};

export const useCreateImage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ documentSuperId, revisionId, superId }: { documentSuperId: number | string; revisionId: string; superId: string }) =>
      api.POST('/documents/{superId}/actions', {
        params: { path: { superId: Number(documentSuperId) } },
        body: { action: 'create_image', data: { revisionId, superId } },
      }),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...documentKeys.imagesAll],
      });
    },

    onError: errorHandler,
  });
};
